import { createGetAPI } from '@cuemath/rest-api';
import { stringify } from 'query-string';
import { BASE_URL } from 'constants/api';

import { ERROR_MESSAGE } from '../constants';

// query-string stringifies array values as repeated params (channels=a&channels=b),
// which the gateway/insightsreader RequestParser (action='append') reads as a list.
const buildURL = endpoint => (_, query) =>
  `${BASE_URL}/website-metrics/${endpoint}/?${stringify(query || {})}`;

// @cuemath/rest-api's useGet returns `error: resource?.error`, but on failure
// the store only ever writes the error under `__ERROR__` — so the hook's own
// `error` is always undefined and failures would render as empty data. Surface a
// failure as a single friendly message so every consumer's error branch works.
// We deliberately never expose the raw backend error to the admin (it can carry
// stack traces / internal detail, and a failure here usually just means the
// Redshift tables are mid-refresh) — the exact HTTP error is still in the
// network tab for debugging.
const deriveError = result => (result.isProcessingFailed ? ERROR_MESSAGE : undefined);

const withError = useGetBase => (...args) => {
  const result = useGetBase(...args);
  return { ...result, error: deriveError(result) };
};

const funnelGet = endpoint =>
  withError(createGetAPI({ getURL: buildURL(endpoint) }).useGet);

const useFunnelSummary = funnelGet('summary');
const useFunnelTimeseries = funnelGet('timeseries');
const useFunnelSubSources = funnelGet('sub-sources');
const useFunnelPages = funnelGet('pages');
const useFunnelExperiment = funnelGet('experiment');
// Ongoing experiment names (from recent website signups) for the name dropdown.
const useFunnelExperimentList = funnelGet('experiment-list');
const useFunnelAllPages = funnelGet('all-pages');
const useFunnelMonthPages = funnelGet('month-pages');
const useFunnelSubSourceDetail = funnelGet('sub-source-detail');
const useFunnelMonthSubSourceDetail = funnelGet('month-sub-source-detail');
const useFunnelLandingPages = funnelGet('landing-pages');
const useFunnelBlog = funnelGet('blog');
const useFunnelMonthBlog = funnelGet('month-blog');
const useFunnelAllPagesDetail = funnelGet('all-pages-detail');
// Returns the object { last_refreshed_utc } (not a list); a failure just hides
// the header note, so the derived `error` here is intentionally left unread.
const useFunnelDataFreshness = funnelGet('data-freshness');

export {
  useFunnelSummary,
  useFunnelTimeseries,
  useFunnelSubSources,
  useFunnelPages,
  useFunnelExperiment,
  useFunnelExperimentList,
  useFunnelAllPages,
  useFunnelMonthPages,
  useFunnelSubSourceDetail,
  useFunnelMonthSubSourceDetail,
  useFunnelLandingPages,
  useFunnelBlog,
  useFunnelMonthBlog,
  useFunnelAllPagesDetail,
  useFunnelDataFreshness,
};
